import {
  Room,
  RoomEvent,
  Track,
  type LocalVideoTrack,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
  type DataPacket_Kind,
} from 'livekit-client';
import { PipecatClient } from '@pipecat-ai/client-js';
import { LiveKitTransport } from '@pipecat-ai/livekit-transport';
import { parseInsightCard, type InsightCardPayload } from './insightCard';
import { api } from './api';
import {
  bindBotPlaybackTrack,
  resumeBotPlaybackMeter,
  unbindBotPlaybackTrack,
} from './botPlaybackMeter';
import { installClientRca } from './clientRca';
import {
  startClientSessionArchive,
  type ClientSessionArchiveHandle,
} from './clientSessionArchive';
import { liveAvatarSpeakText, publishLiveAvatarControl } from './liveAvatarControl';
import { startLiveAvatarSessionLimitTimers } from './liveAvatarSessionLimit';
import { debugLog, installDebugLogGlobals } from './debugLog';
import { reportClientRca } from './reportClientRca';
import { installPulseDiag } from './pulse_diag/install';
import type { AvatarAudioPath } from './sessionUrl';
import {
  isBotIdentity,
  isLiveAvatarIdentity,
  isLivePerceptionIdentity,
} from './participantIdentity';

installDebugLogGlobals();

const RTVI_LABEL = 'rtvi-ai';

/** LiveKitTransport keeps Room private; Pulse needs it for avatar policy / RCA / haircheck. */
function roomFromTransport(transport: LiveKitTransport): Room {
  const room = (transport as unknown as { _room?: Room })._room;
  if (!room) {
    throw new Error('LiveKitTransport room is not initialized');
  }
  return room;
}

/**
 * Pipecat LiveKitTransport elects the first remote as `_botId` and
 * `sendReadyMessage()` waits forever for that identity's mic/cam.
 * When Live Perception joins first, haircheck sticks on "Connecting…" while
 * the real bot already talks. Skip observers; clear a mistaken election.
 */
function installConversationBotGuard(transport: LiveKitTransport): void {
  type TransportBotFields = {
    _botId: string;
    handleParticipantConnected: (p: RemoteParticipant) => void;
    toParticipant: (p: RemoteParticipant) => unknown;
    _callbacks?: {
      onParticipantJoined?: (p: unknown) => void;
      onBotConnected?: (p: unknown) => void;
    };
  };
  const t = transport as unknown as TransportBotFields;
  const original = t.handleParticipantConnected.bind(transport);
  t.handleParticipantConnected = (participant: RemoteParticipant) => {
    if (isLivePerceptionIdentity(participant.identity)) {
      t._callbacks?.onParticipantJoined?.(t.toParticipant(participant));
      debugLog('livekit.skip_perception_as_bot', { identity: participant.identity });
      return;
    }
    if (isLivePerceptionIdentity(t._botId)) {
      debugLog('livekit.clear_perception_bot_id', {
        was: t._botId,
        next: participant.identity,
      });
      t._botId = '';
    }
    original(participant);
  };
}

async function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

function roomHasLiveAvatar(room: Room): boolean {
  for (const p of room.remoteParticipants.values()) {
    if (isLiveAvatarIdentity(p.identity)) return true;
  }
  return false;
}

export type TranscriptLine = {
  role: 'user' | 'assistant';
  text: string;
  at?: number;
  kind?: 'link' | 'text' | 'live_perception_debug';
  url?: string;
  frameUrl?: string;
};

export type SessionHandles = {
  room: Room;
  sessionId: string;
  shortId: string;
  localVideoTrack: LocalVideoTrack | null;
  startedAt: number;
  stopTimers?: () => void;
  emitDiagEndFlow?: (data: Record<string, unknown>) => void;
  /** Present when connected via PipecatClient (Tier C RTVI path). Null for LiveAvatar FULL. */
  pipecatClient?: PipecatClient | null;
  /** LiveAvatar FULL — client owns LaRoom transcripts for Admin archive. */
  agentMode?: 'cascade' | 'direct' | 'liveavatar' | string;
  /** FULL client MediaRecorder archive controller (stop on hangup). */
  clientArchive?: ClientSessionArchiveHandle | null;
  /** LiveAvatar FULL — publish avatar.speak_text on agent-control. */
  speakLiveAvatarText?: (text: string) => Promise<void>;
  /** LiveAvatar FULL — client warn/hard-end policy (also drives stage countdown). */
  sessionPolicy?: { sessionMaxMinutes: number; sessionWarnMinutes: number } | null;
  sessionLimitEndsAtMs?: number | null;
};

const ENDED_STATUSES = new Set(['ended', 'recorded', 'failed', 'completed', 'abandoned', 'upload_failed']);

/** Attach local camera into the PIP container (safe to call after React mounts). */
export function attachLocalPreview(
  track: LocalVideoTrack | null,
  previewEl: HTMLDivElement | null,
): void {
  if (!track || !previewEl) return;
  const existing = previewEl.querySelector('video');
  // NEVER create a second <video> or replaceChildren — React owns the tree.
  // Replacing children causes duplicate local camera players on the next render.
  if (!(existing instanceof HTMLVideoElement)) return;
  existing.muted = true;
  existing.playsInline = true;
  // Already showing haircheck MediaStream — leave srcObject alone.
  if (existing.srcObject) {
    previewEl.classList.add('has-live-preview');
    return;
  }
  track.attach(existing);
  previewEl.classList.add('has-live-preview');
  void existing.play().catch(() => undefined);
}

function shortSessionId(sessionId: string): string {
  const bare = sessionId.replace(/^lk-/, '');
  return bare.slice(0, 8);
}

/** Instantly silence + remove any remote LiveKit audio elements. */
function cleanupRemoteAudio() {
  unbindBotPlaybackTrack();
  document.querySelectorAll(
    'audio[data-lk-remote-audio], audio[data-pulse-client-archive], audio',
  ).forEach((node) => {
    const el = node as HTMLAudioElement;
    try {
      el.muted = true;
      el.volume = 0;
      el.pause();
      el.srcObject = null;
      el.removeAttribute('src');
      el.load();
    } catch {
      /* ignore */
    }
    if (
      el.dataset.lkRemoteAudio === '1'
      || el.dataset.pulseClientArchive
      || el.parentElement === document.body
    ) {
      el.remove();
    }
  });
}

/**
 * Sync mute + detach so Hang up feels instant on the complete screen.
 * Safe to call before await endSession (idempotent with endSession's own cleanup).
 */
export function silenceSessionAudio(handles: SessionHandles | null) {
  if (!handles) {
    cleanupRemoteAudio();
    return;
  }
  cleanupRemoteAudio();
  try {
    if (handles.localVideoTrack) {
      handles.localVideoTrack.stop();
      handles.localVideoTrack.detach().forEach((el) => el.remove());
    }
  } catch {
    /* ignore */
  }
  try {
    void handles.room.localParticipant.setMicrophoneEnabled(false);
  } catch {
    /* ignore */
  }
  try {
    void handles.room.localParticipant.setCameraEnabled(false);
  } catch {
    /* ignore */
  }
  try {
    stopAllLocalTracks(handles.room);
  } catch {
    /* ignore */
  }
  try {
    detachAllRemote(handles.room);
  } catch {
    /* ignore */
  }
  cleanupRemoteAudio();
}

/**
 * LiveAvatar session-limit closing window — stop user uplink without toggling call UI mic state.
 */
export function muteLocalMicForSessionLimit(
  room: Room | null | undefined,
  mediaStream?: MediaStream | null,
) {
  mediaStream?.getAudioTracks().forEach((t) => {
    if (t.readyState !== 'ended') t.enabled = false;
  });
  if (!room) return;
  void room.localParticipant.setMicrophoneEnabled(false).catch(() => undefined);
}

function stopAllLocalTracks(room: Room) {
  try {
    for (const pub of room.localParticipant.trackPublications.values()) {
      const track = pub.track;
      if (!track) continue;
      try {
        track.stop();
      } catch {
        /* ignore */
      }
      try {
        track.detach().forEach((el) => {
          if (el instanceof HTMLMediaElement) {
            el.pause();
            el.srcObject = null;
          }
          el.remove();
        });
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* ignore */
  }
}

function detachAllRemote(room: Room) {
  try {
    for (const p of room.remoteParticipants.values()) {
      for (const pub of p.trackPublications.values()) {
        const track = pub.track;
        if (!track) continue;
        try {
          track.detach().forEach((el) => {
            if (el instanceof HTMLMediaElement) {
              el.muted = true;
              el.volume = 0;
              el.pause();
              el.srcObject = null;
            }
            el.remove();
          });
        } catch {
          /* ignore */
        }
      }
    }
  } catch {
    /* ignore */
  }
}

function hasRemoteBotAudio(room: Room): boolean {
  for (const p of room.remoteParticipants.values()) {
    if (isLivePerceptionIdentity(p.identity)) continue;
    if (!isBotIdentity(p.identity) && !isLiveAvatarIdentity(p.identity)) continue;
    for (const pub of p.audioTrackPublications.values()) {
      if (pub.track && !pub.isMuted) return true;
      if (pub.trackSid) return true;
    }
  }
  return false;
}

async function waitForSessionArming(
  room: Room,
  opts: { timeoutMs: number; requireBotAudio: boolean },
): Promise<{ ok: boolean; botAudio: boolean; sessionArmed: boolean; reason?: string }> {
  let botAudio = hasRemoteBotAudio(room);
  let sessionArmed = false;
  let armedReason: string | undefined;

  if (botAudio && !opts.requireBotAudio) {
    return { ok: true, botAudio, sessionArmed: true, reason: 'already' };
  }

  return await new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean, reason?: string) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      room.off(RoomEvent.TrackSubscribed, onTrack);
      room.off(RoomEvent.TrackPublished, onPublished);
      room.off(RoomEvent.DataReceived, onData);
      resolve({ ok, botAudio, sessionArmed, reason });
    };

    const maybeReady = () => {
      botAudio = botAudio || hasRemoteBotAudio(room);
      // session_armed is the unlock gate. Do not wait for bot/avatar audio —
      // that audio is often the greeting itself (Direct GPT-Live).
      if (sessionArmed) finish(true, armedReason || 'armed');
    };

    const onTrack = (track: RemoteTrack) => {
      if (track.kind === Track.Kind.Audio) {
        botAudio = true;
        maybeReady();
      }
    };
    const onPublished = () => {
      botAudio = botAudio || hasRemoteBotAudio(room);
      maybeReady();
    };
    const onData = (payload: Uint8Array) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload)) as { type?: string; reason?: string };
        if (msg.type === 'session_armed') {
          sessionArmed = true;
          armedReason = msg.reason;
          maybeReady();
        }
      } catch {
        /* ignore */
      }
    };

    room.on(RoomEvent.TrackSubscribed, onTrack);
    room.on(RoomEvent.TrackPublished, onPublished);
    room.on(RoomEvent.DataReceived, onData);
    maybeReady();

    const timer = window.setTimeout(() => {
      botAudio = botAudio || hasRemoteBotAudio(room);
      // Fail-open: always unlock after timeout.
      finish(false, 'timeout');
    }, opts.timeoutMs);
  });
}

type ConnectOpts = {
  videoEl?: HTMLVideoElement | null;
  getVideoEl?: () => HTMLVideoElement | null;
  /** Prefer a getter — call-phase DOM may not exist until after the first paint. */
  previewEl?: HTMLDivElement | null;
  getPreviewEl?: () => HTMLDivElement | null;
  cameraOn: boolean;
  micOn: boolean;
  firstName: string;
  lastName: string;
  agentId?: string;
  /** Admin-only pin: working draft or published snapshot. */
  agentVersionPin?: 'working' | number | null;
  /**
   * Which remote audio to play when LiveAvatar may also publish.
   * Default auto: LiveAvatar when present, else bot TTS (never both).
   */
  avatarAudioPath?: AvatarAudioPath;
  /** Reused haircheck getUserMedia stream (tracks stay live). */
  mediaStream?: MediaStream | null;
  /** Admin Cognito only — show Live Perception debug transcript rows. */
  showLivePerceptionDebug?: boolean;
  onTranscript?: (line: TranscriptLine) => void;
  onDisconnected?: () => void;
  /** Fired after room connect + local A/V publish, before bot arming wait. */
  onConnected?: () => void;
  onArmingTimeout?: () => void;
  /** Browser refused to play the agent's audio (true) or playback recovered (false). */
  onAudioBlocked?: (blocked: boolean) => void;
  /** Skip haircheck arming wait (soft rejoin when bot already live). */
  skipArmingWait?: boolean;
  /** LiveAvatar FULL — agent session max / warn (from POST /sessions/livekit). */
  sessionPolicy?: { sessionMaxMinutes: number; sessionWarnMinutes: number } | null;
  /** Wall-clock cap (ms) stamped at LA mint (POST /sessions/livekit). */
  sessionLimitEndsAtMs?: number;
  /** LiveAvatar FULL — safety fallback if LiveKit never disconnects after mint cap. */
  onSessionLimitEnd?: () => void;
  /** Direct live insight card (pulse.insight_card room data). */
  onInsightCard?: (card: InsightCardPayload) => void;
  /**
   * Fired right after POST /sessions/livekit succeeds (before room connect).
   * Use for session-stamped flags such as liveInsightCardsEnabled.
   */
  onSessionCreated?: (meta: { liveInsightCardsEnabled?: boolean }) => void;
};

/** Unlock agent audio from a user gesture after the browser blocked autoplay. */
export async function resumeRemoteAudio(room: Room | null | undefined): Promise<void> {
  if (!room) return;
  try {
    await room.startAudio();
  } catch (err: unknown) {
    debugLog('remote_audio.start_audio_failed', {
      message: err instanceof Error ? err.message : String(err),
    });
  }
  resumeBotPlaybackMeter();
  document
    .querySelectorAll<HTMLAudioElement>('audio[data-lk-remote-audio="1"]')
    .forEach((el) => {
      void el.play().catch(() => undefined);
    });
}

type RoomCreds = {
  sessionId: string;
  url: string;
  userToken: string;
  /** When false, skip POST /ready (bot already queued / in room). */
  enqueueBot: boolean;
  /** Seed chat from server (rejoin). */
  transcriptLines?: Array<{
    role?: string;
    text?: string;
    at?: number;
    kind?: string;
    url?: string;
  }>;
  /** LiveAvatar FULL + agent Perception toggle. */
  clientSessionArchiveEnabled?: boolean;
};

async function connectLiveKitRoom(creds: RoomCreds, opts: ConnectOpts): Promise<SessionHandles> {
  const resolvePreview = () => opts.getPreviewEl?.() ?? opts.previewEl ?? null;
  const resolveVideo = () => opts.getVideoEl?.() ?? opts.videoEl ?? null;
  const displayName = `${opts.firstName} ${opts.lastName}`.trim();

  // dual-PC / v0 path works with LiveKit server 1.9.x (no /rtc/v1/validate)
  // dtx/red off: LiveKit defaults are true; Track Egress user.ogg was wedging to
  // silence after the first speech burst while STT still heard the mic.
  const transport = new LiveKitTransport({
    adaptiveStream: true,
    dynacast: true,
    singlePeerConnection: false,
    audioCaptureDefaults: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    publishDefaults: {
      dtx: false,
      red: false,
    },
  });
  installConversationBotGuard(transport);
  const room = roomFromTransport(transport);

  let resolveLocalReady: () => void = () => undefined;
  const localReady = new Promise<void>((resolve) => {
    resolveLocalReady = resolve;
  });

  const pipecatClient = new PipecatClient({
    transport,
    // Haircheck MediaStream is published onto the Room in onConnected.
    enableMic: false,
    enableCam: false,
    disconnectOnBotDisconnect: false,
    callbacks: {
      onConnected: () => {
        void (async () => {
          try {
            if (displayName) {
              await room.localParticipant.setName(displayName).catch(() => undefined);
            }
            const stream = opts.mediaStream;
            const audioTrack = stream?.getAudioTracks()[0];
            const videoTrack = stream?.getVideoTracks()[0];
            if (opts.micOn) {
              if (audioTrack && audioTrack.readyState !== 'ended') {
                await room.localParticipant.publishTrack(audioTrack, {
                  source: Track.Source.Microphone,
                  name: 'microphone',
                  dtx: false,
                  red: false,
                });
              } else {
                await room.localParticipant.setMicrophoneEnabled(true, undefined, {
                  dtx: false,
                  red: false,
                });
              }
            }
            if (opts.cameraOn) {
              if (videoTrack && videoTrack.readyState !== 'ended') {
                await room.localParticipant.publishTrack(videoTrack, {
                  source: Track.Source.Camera,
                  name: 'camera',
                });
              } else {
                await room.localParticipant.setCameraEnabled(true);
              }
            }
            // Enqueue bot while sendReadyMessage waits for bot media / RTVI handshake.
            if (creds.enqueueBot) {
              await api.signalSessionReady(creds.sessionId);
            }
            opts.onConnected?.();
          } catch (err: unknown) {
            debugLog('pipecat.on_connected_failed', {
              message: err instanceof Error ? err.message : String(err),
            });
          } finally {
            resolveLocalReady();
          }
        })();
      },
      onUserTranscript: (data) => {
        const text = (data?.text || '').trim();
        if (!text) return;
        const extra = data as { aggregated_by?: string; final?: boolean };
        const agg = String(extra.aggregated_by || '');
        if (agg === 'word' || agg === 'token') return;
        if (extra.final === false) return;
        opts.onTranscript?.({ role: 'user', text });
      },
      onBotOutput: (data) => {
        const text = (data?.text || '').trim();
        if (!text) return;
        const agg = String(data?.aggregated_by || '');
        // Skip word/token floods; sentence (and custom aggregators) drive the chat bubble.
        if (agg === 'word' || agg === 'token') return;
        opts.onTranscript?.({ role: 'assistant', text });
      },
    },
  });

  // Report each playback problem once per session so review can separate
  // "user could not hear the agent" from server-side audio issues.
  const reportedAudio = new Set<string>();
  const reportClientAudio = (
    type: 'audio_blocked' | 'audio_play_failed' | 'audio_resumed',
    message?: string,
  ) => {
    if (reportedAudio.has(type)) return;
    reportedAudio.add(type);
    void api.reportClientEvent(creds.sessionId, { type, message }).catch(() => undefined);
  };
  let audioBlocked = false;
  const markAudioBlocked = (type: 'audio_blocked' | 'audio_play_failed', message?: string) => {
    reportClientAudio(type, message);
    if (audioBlocked) return;
    audioBlocked = true;
    opts.onAudioBlocked?.(true);
  };
  const markAudioResumed = () => {
    if (!audioBlocked) return;
    audioBlocked = false;
    reportClientAudio('audio_resumed');
    opts.onAudioBlocked?.(false);
  };
  room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
    debugLog('remote_audio.playback_status', { canPlaybackAudio: room.canPlaybackAudio });
    if (room.canPlaybackAudio) markAudioResumed();
    else markAudioBlocked('audio_blocked');
  });

  /** Prefer LiveAvatar / bot camera over incidental remote video. */
  let attachedVideoRank = -1;
  const videoIdentityRank = (identity: string | undefined) => {
    const id = (identity || '').toLowerCase();
    if (id.includes('liveavatar') || id.includes('heygen') || id.includes('avatar')) return 2;
    if (id.includes('bot') || id.includes('agent')) return 1;
    return 0;
  };

  const avatarAudioPath: AvatarAudioPath = opts.avatarAudioPath || 'auto';
  type AudioSlot = { el: HTMLAudioElement; kind: 'liveavatar' | 'bot' | 'other'; track: RemoteTrack };
  const audioSlots: AudioSlot[] = [];
  // Haircheck stays on `connecting` until arming wait returns. Remote tracks
  // exist (and would autoplay) before the call UI — keep them silent until then.
  let remotePlaybackAllowed = Boolean(opts.skipArmingWait);

  const setSlotAudible = (slot: AudioSlot, audible: boolean) => {
    const wasAudible = !slot.el.muted && slot.el.volume > 0;
    slot.el.muted = !audible;
    slot.el.volume = audible ? 1 : 0;
    if (!audible && wasAudible) {
      try {
        slot.el.pause();
      } catch {
        /* ignore */
      }
    } else if (audible && (slot.el.paused || !wasAudible)) {
      void slot.el.play().catch(() => undefined);
    }
  };

  /** Never play bot TTS and LiveAvatar audio together (echo → fake user turns). */
  const applyAvatarAudioPolicy = (reason: string) => {
    const hasLa = audioSlots.some((s) => s.kind === 'liveavatar') || roomHasLiveAvatar(room);
    for (const slot of audioSlots) {
      let audible = true;
      if (!remotePlaybackAllowed) {
        audible = false;
      } else if (avatarAudioPath === 'tts') {
        audible = slot.kind !== 'liveavatar';
      } else if (avatarAudioPath === 'liveavatar') {
        audible = slot.kind === 'liveavatar';
      } else {
        // auto: LiveAvatar wins when present; otherwise bot TTS.
        if (slot.kind === 'liveavatar') audible = true;
        else if (slot.kind === 'bot') audible = !hasLa;
        else audible = !hasLa;
      }
      setSlotAudible(slot, audible);
    }
    // Meter the audible path only.
    const preferred =
      audioSlots.find((s) => s.kind === 'liveavatar' && !s.el.muted)
      || audioSlots.find((s) => s.kind === 'bot' && !s.el.muted)
      || audioSlots.find((s) => !s.el.muted);
    try {
      unbindBotPlaybackTrack();
      const mst = preferred?.track.mediaStreamTrack;
      if (mst) bindBotPlaybackTrack(mst);
    } catch {
      /* ignore */
    }
    debugLog('remote_audio.avatar_policy', {
      path: avatarAudioPath,
      hasLa,
      reason,
      slots: audioSlots.map((s) => ({ kind: s.kind, muted: s.el.muted })),
    });
  };

  const attachRemote = (track: RemoteTrack, participant?: RemoteParticipant) => {
    if (track.kind === Track.Kind.Audio) {
      const el = track.attach() as HTMLAudioElement;
      el.autoplay = remotePlaybackAllowed;
      el.muted = !remotePlaybackAllowed;
      el.volume = remotePlaybackAllowed ? 1 : 0;
      el.dataset.lkRemoteAudio = '1';
      const identity = participant?.identity || '';
      const kind: AudioSlot['kind'] = isLiveAvatarIdentity(identity)
        ? 'liveavatar'
        : isBotIdentity(identity)
          ? 'bot'
          : 'other';
      el.dataset.lkAudioKind = kind;
      document.body.appendChild(el);
      const slot: AudioSlot = { el, kind, track };
      audioSlots.push(slot);
      const trackId = track.sid || 'audio';
      debugLog('remote_audio.attach', { trackId, identity, kind, avatarAudioPath });
      reportClientRca(creds.sessionId, 'remote_audio_attach', {
        trackId,
        identity,
        kind,
        avatarAudioPath,
        elementMuted: el.muted ? 1 : 0,
      });
      applyAvatarAudioPolicy('attach');
      void el
        .play()
        .then(() => {
          debugLog('remote_audio.play_ok', {
            trackId,
            paused: el.paused,
            muted: el.muted,
            volume: el.volume,
            kind,
          });
          reportClientRca(creds.sessionId, 'remote_audio_play_ok', {
            trackId,
            identity,
            kind,
            paused: el.paused ? 1 : 0,
            elementMuted: el.muted ? 1 : 0,
          });
        })
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : String(err);
          debugLog('remote_audio.play_failed', { trackId, message, kind });
          reportClientRca(creds.sessionId, 'remote_audio_play_failed', {
            trackId,
            identity,
            kind,
          }, message);
          // Muted policy tracks need not surface as "audio blocked".
          if (el.muted) return;
          window.setTimeout(() => {
            void el.play().then(
              () => {
                debugLog('remote_audio.play_retry_ok', { trackId });
                reportClientRca(creds.sessionId, 'remote_audio_play_retry_ok', { trackId, kind });
              },
              (e2: unknown) => {
                const retryMessage = e2 instanceof Error ? e2.message : String(e2);
                debugLog('remote_audio.play_retry_failed', { trackId, message: retryMessage });
                reportClientRca(creds.sessionId, 'remote_audio_play_retry_failed', {
                  trackId,
                  kind,
                }, retryMessage);
                markAudioBlocked('audio_play_failed', retryMessage);
              },
            );
          }, 250);
        });
      el.addEventListener('playing', () => {
        debugLog('remote_audio.playing', { trackId, currentTime: el.currentTime, kind });
        if (!el.muted) markAudioResumed();
      });
      el.addEventListener('pause', () => {
        debugLog('remote_audio.pause', { trackId, currentTime: el.currentTime, kind });
      });
      el.addEventListener('error', () => {
        debugLog('remote_audio.element_error', {
          trackId,
          code: el.error?.code,
          message: el.error?.message,
          kind,
        });
      });
      return;
    }
    if (track.kind === Track.Kind.Video) {
      const rank = videoIdentityRank(participant?.identity);
      if (rank < attachedVideoRank) return;
      const videoEl = resolveVideo();
      if (!videoEl) return;
      track.attach(videoEl);
      attachedVideoRank = rank;
      videoEl.style.display = 'block';
      videoEl.classList.add('has-remote-video');
      videoEl.parentElement?.classList.add('showing-remote-video');
      const refImg = videoEl.parentElement?.querySelector('.video-reference');
      if (refImg instanceof HTMLElement) {
        refImg.style.opacity = '0';
        refImg.removeAttribute('hidden');
      }
      debugLog('remote_video.attach', {
        trackId: track.sid || 'video',
        identity: participant?.identity || '',
        rank,
      });
      reportClientRca(creds.sessionId, 'remote_video_attach', {
        trackId: track.sid || 'video',
        identity: participant?.identity || '',
      });
    }
  };

  /** When LiveAvatar video dies, restore the agent cover poster (audio falls back to TTS). */
  const clearRemoteVideoIfNeeded = (reason: string, identity?: string) => {
    const videoEl = resolveVideo();
    if (!videoEl || attachedVideoRank < 0) return;
    const laGone = identity ? isLiveAvatarIdentity(identity) : false;
    const showingLa = attachedVideoRank >= 2;
    // Only restore cover for LiveAvatar teardown (not user/camera tracks).
    if (!laGone && !showingLa) return;
    if (identity && !laGone && videoIdentityRank(identity) !== attachedVideoRank) return;
    try {
      videoEl.srcObject = null;
      videoEl.removeAttribute('src');
      videoEl.load();
    } catch {
      /* ignore */
    }
    videoEl.style.display = 'none';
    videoEl.classList.remove('has-remote-video');
    videoEl.parentElement?.classList.remove('showing-remote-video');
    attachedVideoRank = -1;
    const refImg = videoEl.parentElement?.querySelector('.video-reference');
    if (refImg instanceof HTMLElement) {
      refImg.style.opacity = '1';
      refImg.removeAttribute('hidden');
    }
    debugLog('remote_video.clear', { reason, identity: identity || '' });
  };

  const dropLiveAvatarAudioSlots = () => {
    for (let i = audioSlots.length - 1; i >= 0; i -= 1) {
      if (audioSlots[i].kind !== 'liveavatar') continue;
      const [removed] = audioSlots.splice(i, 1);
      try {
        removed.el.muted = true;
        removed.el.remove();
      } catch {
        /* ignore */
      }
    }
  };

  room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, p: RemoteParticipant) => {
    attachRemote(track, p);
  });

  // Mute bot early when LiveAvatar joins (video may arrive before audio).
  room.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
    if (isLiveAvatarIdentity(p.identity)) applyAvatarAudioPolicy('liveavatar_joined');
  });
  room.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
    if (isLiveAvatarIdentity(p.identity)) {
      // Drop LA audio immediately so auto path unmutes bot TTS (idle nudges, next turns).
      dropLiveAvatarAudioSlots();
      applyAvatarAudioPolicy('liveavatar_left');
      clearRemoteVideoIfNeeded('liveavatar_left', p.identity);
    }
  });

  room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, _pub?: RemoteTrackPublication, p?: RemoteParticipant) => {
    const before = audioSlots.length;
    for (let i = audioSlots.length - 1; i >= 0; i -= 1) {
      if (audioSlots[i].track !== track) continue;
      const [removed] = audioSlots.splice(i, 1);
      try {
        removed.el.muted = true;
        removed.el.remove();
      } catch {
        /* ignore */
      }
    }
    if (audioSlots.length !== before) applyAvatarAudioPolicy('unsubscribe');
    if (track.kind === Track.Kind.Video) {
      clearRemoteVideoIfNeeded('track_unsubscribed', p?.identity);
    }
  });

  // Pulse extras only — RTVI chat is handled by PipecatClient (label rtvi-ai).
  room.on(RoomEvent.DataReceived, (payload: Uint8Array, _p?: RemoteParticipant, _kind?: DataPacket_Kind) => {
    try {
      const msg = JSON.parse(new TextDecoder().decode(payload)) as {
        label?: string;
        type?: string;
        role?: string;
        text?: string;
        at?: number;
        kind?: string;
        frameUrl?: string;
        url?: string;
      };
      if (msg.label === RTVI_LABEL) return;
      if (msg.type === 'session_armed' || msg.type === 'pulse_live_perception') return;
      const insight = parseInsightCard(msg);
      if (insight) {
        opts.onInsightCard?.(insight);
        return;
      }
      if (msg.type?.startsWith('pulse_') || msg.type?.startsWith('pulse.')) return;
      if (!opts.onTranscript) return;
      if (msg.type !== 'transcript' || !msg.text?.trim()) return;
      if (msg.kind === 'live_perception_debug') {
        if (!opts.showLivePerceptionDebug) return;
        opts.onTranscript({
          role: 'assistant',
          text: msg.text.trim(),
          at: msg.at,
          kind: 'live_perception_debug',
          frameUrl: msg.frameUrl || msg.url,
        });
        return;
      }
      if (msg.kind === 'link') {
        opts.onTranscript({
          role: 'assistant',
          text: msg.text.trim(),
          at: msg.at,
          kind: 'link',
          url: msg.url || msg.text.trim(),
        });
        return;
      }
      // Normal chat text is RTVI-only; ignore legacy type=transcript text packets.
    } catch {
      /* ignore non-json */
    }
  });

  // Guard + fail-open: without this, a perception-* `_botId` makes sendReadyMessage hang forever.
  await withTimeout(
    pipecatClient.connect({ url: creds.url, token: creds.userToken }),
    20_000,
    'LiveKit connect',
  );
  await localReady;

  let localVideoTrack: LocalVideoTrack | null = null;
  if (opts.cameraOn) {
    const camPub = [...room.localParticipant.videoTrackPublications.values()][0];
    localVideoTrack = (camPub?.track as LocalVideoTrack) || null;
    // CallPage already owns a <video srcObject={mediaStream}>. Never create another.
    if (!opts.mediaStream) {
      attachLocalPreview(localVideoTrack, resolvePreview());
    }
  }

  for (const p of room.remoteParticipants.values()) {
    for (const pub of p.trackPublications.values()) {
      if (pub.track) attachRemote(pub.track, p);
    }
  }

  if (!opts.skipArmingWait && creds.enqueueBot) {
    const armed = await waitForSessionArming(room, {
      timeoutMs: 10_000,
      requireBotAudio: false,
    });
    remotePlaybackAllowed = true;
    applyAvatarAudioPolicy('armed');
    if (!armed.ok) {
      debugLog('session_arming.timeout', {
        sessionId: creds.sessionId,
        botAudio: armed.botAudio,
        sessionArmed: armed.sessionArmed,
      });
      opts.onArmingTimeout?.();
    } else {
      debugLog('session_arming.ready', {
        sessionId: creds.sessionId,
        reason: armed.reason,
      });
    }
  } else {
    remotePlaybackAllowed = true;
    applyAvatarAudioPolicy('no_arm_wait');
  }

  const heartbeat = window.setInterval(() => {
    void api.postActivity(creds.sessionId).catch(() => undefined);
  }, 15000);

  const stopClientRca = installClientRca(room, creds.sessionId);
  const pulseDiag = installPulseDiag(room, creds.sessionId, null);

  // One-shot archive seed (rejoin / late join). Live chat grows via RTVI only.
  const emitLines = (
    lines: Array<{ role?: string; text?: string; at?: number; kind?: string; url?: string; frameUrl?: string }>,
  ) => {
    if (!opts.onTranscript) return;
    for (const line of lines) {
      if (!line?.text && !line?.url) continue;
      const kind =
        line.kind === 'link'
          ? 'link'
          : line.kind === 'live_perception_debug'
            ? 'live_perception_debug'
            : undefined;
      if (kind === 'live_perception_debug' && !opts.showLivePerceptionDebug) continue;
      opts.onTranscript({
        role: line.role === 'user' ? 'user' : 'assistant',
        text: line.text || line.url || '',
        at: line.at,
        kind,
        url: line.url,
        frameUrl: (line as { frameUrl?: string }).frameUrl || line.url,
      });
    }
  };
  if (creds.transcriptLines?.length) {
    emitLines(creds.transcriptLines);
  }

  // Status-only poll — do not drive chat from transcriptLines (Dynamo is archive).
  let pollBusy = false;
  let endedNotified = false;
  let pollId = 0;
  const stopTimers = () => {
    window.clearInterval(heartbeat);
    if (pollId) window.clearInterval(pollId);
    stopClientRca();
    pulseDiag.dispose();
  };
  const notifyEnded = () => {
    if (endedNotified) return;
    endedNotified = true;
    stopTimers();
    cleanupRemoteAudio();
    opts.onDisconnected?.();
  };
  const pullStatus = () => {
    if (pollBusy) return;
    pollBusy = true;
    void api
      .getSessionLive(creds.sessionId)
      .then((live) => {
        if (live.status && ENDED_STATUSES.has(String(live.status))) {
          notifyEnded();
        }
      })
      .catch(() => undefined)
      .finally(() => {
        pollBusy = false;
      });
  };
  pollId = window.setInterval(pullStatus, 1200);
  pullStatus();

  // Bot hangup cancels the worker — user may remain alone in the room.
  // Only end after we had a remote (agent) and the room stays empty for a while.
  // Longer debounce so soft-rejoin refresh doesn't race the bot still present.
  let sawRemote = room.remoteParticipants.size > 0;
  let emptyTimer: number | undefined;
  room.on(RoomEvent.ParticipantConnected, () => {
    sawRemote = true;
    if (emptyTimer) {
      window.clearTimeout(emptyTimer);
      emptyTimer = undefined;
    }
  });
  room.on(RoomEvent.ParticipantDisconnected, () => {
    if (!sawRemote) return;
    if (emptyTimer) window.clearTimeout(emptyTimer);
    emptyTimer = window.setTimeout(() => {
      emptyTimer = undefined;
      if (room.remoteParticipants.size === 0) notifyEnded();
    }, 2500);
  });

  room.on(RoomEvent.Disconnected, () => {
    if (emptyTimer) window.clearTimeout(emptyTimer);
    notifyEnded();
  });

  return {
    room,
    sessionId: creds.sessionId,
    shortId: shortSessionId(creds.sessionId),
    localVideoTrack,
    startedAt: Date.now(),
    stopTimers,
    emitDiagEndFlow: pulseDiag.emitEndFlow,
    pipecatClient,
  };
}

/** LiveAvatar FULL (LaRoom): plain LiveKit join — no Pipecat RTVI / Pulse mouth. */
async function connectLiveAvatarFullRoom(
  creds: RoomCreds,
  opts: ConnectOpts,
): Promise<SessionHandles> {
  const resolvePreview = () => opts.getPreviewEl?.() ?? opts.previewEl ?? null;
  const resolveVideo = () => opts.getVideoEl?.() ?? opts.videoEl ?? null;
  const displayName = `${opts.firstName} ${opts.lastName}`.trim();
  const room = new Room({
    adaptiveStream: true,
    dynacast: true,
  });

  /** Pending video when CallPage <video> is not mounted yet (haircheck → morph). */
  let pendingVideo: { track: RemoteTrack; identity?: string } | null = null;
  let videoRetryId = 0;

  /** Match Cascade remote attach: cover is position:absolute z-index:0 and paints over in-flow <video>. */
  const revealRemoteVideoEl = (videoEl: HTMLVideoElement, track: RemoteTrack, identity?: string) => {
    track.attach(videoEl);
    videoEl.style.display = 'block';
    videoEl.classList.add('has-remote-video');
    videoEl.parentElement?.classList.add('showing-remote-video');
    const refImg = videoEl.parentElement?.querySelector('.video-reference');
    if (refImg instanceof HTMLElement) {
      refImg.style.opacity = '0';
      refImg.removeAttribute('hidden');
    }
    void videoEl.play().catch(() => undefined);
    debugLog('liveavatar.full.video_attached', { identity });
    reportClientRca(creds.sessionId, 'remote_video_attach', {
      identity: identity || '',
      mode: 'liveavatar',
    });
  };

  const attachRemoteVideo = (track: RemoteTrack, identity?: string) => {
    if (track.kind !== Track.Kind.Video) return;
    const el = resolveVideo();
    if (!el) {
      pendingVideo = { track, identity };
      if (!videoRetryId) {
        let tries = 0;
        videoRetryId = window.setInterval(() => {
          tries += 1;
          const pending = pendingVideo;
          const videoEl = resolveVideo();
          if (pending && videoEl) {
            revealRemoteVideoEl(videoEl, pending.track, pending.identity);
            debugLog('liveavatar.full.video_attached_retry', {
              identity: pending.identity,
              tries,
            });
            pendingVideo = null;
            window.clearInterval(videoRetryId);
            videoRetryId = 0;
            return;
          }
          if (tries >= 40) {
            window.clearInterval(videoRetryId);
            videoRetryId = 0;
          }
        }, 250);
      }
      return;
    }
    revealRemoteVideoEl(el, track, identity);
    pendingVideo = null;
  };

  const attachRemoteAudio = (track: RemoteTrack, identity?: string) => {
    if (track.kind !== Track.Kind.Audio) return;
    const el = track.attach() as HTMLAudioElement;
    el.autoplay = true;
    el.dataset.lkRemoteAudio = '1';
    el.dataset.lkAudioKind = isLiveAvatarIdentity(identity || '') ? 'liveavatar' : 'bot';
    document.body.appendChild(el);
    const trackId = track.sid || 'audio';
    reportClientRca(creds.sessionId, 'remote_audio_attach', {
      trackId,
      identity: identity || '',
      kind: el.dataset.lkAudioKind,
      mode: 'liveavatar',
    });
    void el.play().then(
      () => reportClientRca(creds.sessionId, 'remote_audio_play_ok', {
        trackId,
        identity: identity || '',
        kind: el.dataset.lkAudioKind,
        mode: 'liveavatar',
      }),
      (err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        reportClientRca(creds.sessionId, 'remote_audio_play_failed', {
          trackId,
          identity: identity || '',
          mode: 'liveavatar',
        }, message);
      },
    );
  };

  room.on(RoomEvent.TrackSubscribed, (track, _pub, p) => {
    if (track.kind === Track.Kind.Video) attachRemoteVideo(track, p?.identity);
    if (track.kind === Track.Kind.Audio) attachRemoteAudio(track, p?.identity);
  });

  room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
    if (!opts.onTranscript) return;
    try {
      const msg = JSON.parse(new TextDecoder().decode(payload)) as {
        event_type?: string;
        type?: string;
        text?: string;
        payload?: { text?: string };
      };
      const et = String(msg.event_type || msg.type || '').toLowerCase();
      const text = String(msg.text || msg.payload?.text || '').trim();
      if (!text) return;
      const at = Date.now();
      if (et === 'user.transcription' || et === 'user_transcription') {
        opts.onTranscript({ role: 'user', text, at });
        void api
          .postTranscriptLine(creds.sessionId, { role: 'user', text, at })
          .catch(() => undefined);
      } else if (
        et === 'avatar.transcription' ||
        et === 'avatar_transcription' ||
        et === 'agent.transcription'
      ) {
        opts.onTranscript({ role: 'assistant', text, at });
        void api
          .postTranscriptLine(creds.sessionId, { role: 'assistant', text, at })
          .catch(() => undefined);
      }
    } catch {
      /* ignore */
    }
  });

  await room.connect(creds.url, creds.userToken);
  // Avatar may already be publishing — Cascade path does the same walk.
  for (const p of room.remoteParticipants.values()) {
    for (const pub of p.trackPublications.values()) {
      if (!pub.track) continue;
      if (pub.track.kind === Track.Kind.Video) attachRemoteVideo(pub.track, p.identity);
      if (pub.track.kind === Track.Kind.Audio) attachRemoteAudio(pub.track, p.identity);
    }
  }
  if (displayName) {
    await room.localParticipant.setName(displayName).catch(() => undefined);
  }
  const stream = opts.mediaStream;
  const audioTrack = stream?.getAudioTracks()[0];
  const videoTrack = stream?.getVideoTracks()[0];
  if (opts.micOn) {
    if (audioTrack && audioTrack.readyState !== 'ended') {
      await room.localParticipant.publishTrack(audioTrack, {
        source: Track.Source.Microphone,
        name: 'microphone',
      });
    } else {
      await room.localParticipant.setMicrophoneEnabled(true);
    }
  }
  if (opts.cameraOn) {
    if (videoTrack && videoTrack.readyState !== 'ended') {
      await room.localParticipant.publishTrack(videoTrack, {
        source: Track.Source.Camera,
        name: 'camera',
      });
    } else {
      await room.localParticipant.setCameraEnabled(true);
    }
  }
  const preview = resolvePreview();
  if (preview && stream) {
    const video = preview.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      video.muted = true;
      video.playsInline = true;
      video.srcObject = stream;
      void video.play().catch(() => undefined);
      preview.classList.add('has-live-preview');
    }
  }

  if (creds.enqueueBot) {
    await api.signalSessionReady(creds.sessionId);
  }

  const stopClientRca = installClientRca(room, creds.sessionId);

  let clientArchive: ClientSessionArchiveHandle | null = null;
  if (creds.clientSessionArchiveEnabled === true) {
    try {
      clientArchive = startClientSessionArchive({
        sessionId: creds.sessionId,
        room,
        mediaStream: opts.mediaStream || null,
      });
      reportClientRca(creds.sessionId, 'archive_enabled', {
        mode: 'liveavatar',
        joinCameraOn: opts.cameraOn === true,
      });
    } catch (e) {
      reportClientRca(creds.sessionId, 'archive_error', {
        mode: 'liveavatar',
        phase: 'start',
      }, String((e as Error)?.message || e));
      clientArchive = null;
    }
  }

  opts.onConnected?.();

  let sessionLimitHandle: ReturnType<typeof startLiveAvatarSessionLimitTimers> | undefined;
  if (opts.sessionPolicy && opts.sessionPolicy.sessionMaxMinutes > 0) {
    const maxM = opts.sessionPolicy.sessionMaxMinutes;
    const fallbackEndsAt = Date.now() + maxM * 60_000;
    const endsAtMs =
      typeof opts.sessionLimitEndsAtMs === 'number' && opts.sessionLimitEndsAtMs > 0
        ? opts.sessionLimitEndsAtMs
        : fallbackEndsAt;
    sessionLimitHandle = startLiveAvatarSessionLimitTimers(
      room,
      {
        sessionMaxMinutes: opts.sessionPolicy.sessionMaxMinutes,
        sessionWarnMinutes: opts.sessionPolicy.sessionWarnMinutes,
      },
      () => opts.onSessionLimitEnd?.(),
      endsAtMs,
      {
        sessionId: creds.sessionId,
        onEnterClosing: () => {
          muteLocalMicForSessionLimit(room, stream);
          reportClientRca(creds.sessionId, 'session_limit_mic_muted', {
            roomState: String(room.state || ''),
          });
          void publishLiveAvatarControl(room, 'avatar.stop_listening', {});
        },
      },
    );
  } else if (creds.sessionId) {
    reportClientRca(creds.sessionId, 'session_limit_timers_skipped', {
      reason: 'missing_session_policy',
      mode: 'liveavatar',
    });
  }

  room.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
    if (isLiveAvatarIdentity(p.identity)) {
      sessionLimitHandle?.onLiveAvatarParticipantLeft();
    }
  });

  let ended = false;
  const notifyEnded = () => {
    if (ended) return;
    ended = true;
    if (videoRetryId) {
      window.clearInterval(videoRetryId);
      videoRetryId = 0;
    }
    pendingVideo = null;
    try {
      stopTimers();
    } catch {
      /* ignore */
    }
    opts.onDisconnected?.();
  };
  room.on(RoomEvent.Disconnected, () => notifyEnded());

  const stopTimers = () => {
    sessionLimitHandle?.dispose();
    sessionLimitHandle = undefined;
    if (videoRetryId) {
      window.clearInterval(videoRetryId);
      videoRetryId = 0;
    }
    pendingVideo = null;
    stopClientRca();
  };

  return {
    room,
    sessionId: creds.sessionId,
    shortId: shortSessionId(creds.sessionId),
    localVideoTrack: null,
    startedAt: Date.now(),
    stopTimers,
    emitDiagEndFlow: () => undefined,
    pipecatClient: null,
    clientArchive,
    speakLiveAvatarText: (text: string) => liveAvatarSpeakText(room, text),
    sessionPolicy: opts.sessionPolicy || null,
    sessionLimitEndsAtMs: sessionLimitHandle?.endsAtMs ?? opts.sessionLimitEndsAtMs ?? null,
  };
}

export async function startLiveKitSession(opts: ConnectOpts): Promise<SessionHandles> {
  const displayName = `${opts.firstName} ${opts.lastName}`.trim();
  const avatarAudioPath: AvatarAudioPath = opts.avatarAudioPath || 'auto';
  // Create room + tokens only — bot is enqueued after A/V is up (ready gate).
  const created = await api.createLiveKitSession({
    spawnBot: false,
    firstName: opts.firstName,
    lastName: opts.lastName,
    userName: displayName,
    agentId: opts.agentId || undefined,
    avatarAudioPath,
    ...(opts.agentVersionPin != null
      ? {
          adminPreview: true,
          agentConfigVersion:
            opts.agentVersionPin === 'working' ? 0 : opts.agentVersionPin,
        }
      : {}),
  });
  try {
    opts.onSessionCreated?.({
      liveInsightCardsEnabled: created.liveInsightCardsEnabled === true,
    });
  } catch {
    /* ignore */
  }
  const creds: RoomCreds = {
    sessionId: created.sessionId,
    url: created.url,
    userToken: created.userToken,
    enqueueBot: true,
    clientSessionArchiveEnabled: created.clientSessionArchiveEnabled === true,
  };
  if (
    created.liveAvatarFull === true ||
    String(created.agentMode || '').toLowerCase() === 'liveavatar'
  ) {
    const sessionPolicy =
      typeof created.sessionMaxMinutes === 'number'
        ? {
            sessionMaxMinutes: created.sessionMaxMinutes,
            sessionWarnMinutes:
              typeof created.sessionWarnMinutes === 'number' ? created.sessionWarnMinutes : 1,
          }
        : opts.sessionPolicy;
    const sessionLimitEndsAtMs =
      typeof created.sessionLimitEndsAtMs === 'number' ? created.sessionLimitEndsAtMs : undefined;
    const handles = await connectLiveAvatarFullRoom(creds, {
      ...opts,
      sessionPolicy,
      sessionLimitEndsAtMs,
    });
    handles.agentMode = 'liveavatar';
    if (sessionPolicy) handles.sessionPolicy = sessionPolicy;
    return handles;
  }
  return connectLiveKitRoom(creds, opts);
}

/** Soft rejoin — same room/bot, new user token. No second enqueue when bot is live. */
export async function rejoinLiveKitSession(
  sessionId: string,
  opts: ConnectOpts,
): Promise<SessionHandles> {
  const displayName = `${opts.firstName} ${opts.lastName}`.trim();
  const rejoined = await api.rejoinSession(sessionId, {
    firstName: opts.firstName,
    lastName: opts.lastName,
    userName: displayName,
  });
  return connectLiveKitRoom(
    {
      sessionId: rejoined.sessionId,
      url: rejoined.url,
      userToken: rejoined.userToken,
      enqueueBot: Boolean(rejoined.needsReady),
      transcriptLines: rejoined.transcriptLines,
    },
    opts,
  );
}

export async function setMicEnabled(room: Room, enabled: boolean) {
  await room.localParticipant.setMicrophoneEnabled(enabled, undefined, {
    dtx: false,
    red: false,
  });
}

export async function setCameraEnabled(
  room: Room,
  enabled: boolean,
  previewEl: HTMLDivElement | null,
): Promise<LocalVideoTrack | null> {
  await room.localParticipant.setCameraEnabled(enabled);
  if (!enabled) {
    // Never wipe React-owned <video> children — CallPage toggles visibility.
    return null;
  }
  const camPub = [...room.localParticipant.videoTrackPublications.values()][0];
  const track = (camPub?.track as LocalVideoTrack) || null;
  attachLocalPreview(track, previewEl);
  return track;
}

/** Max wait for client MediaRecorder multipart finalize before hangup tears down LaRoom. */
const CLIENT_ARCHIVE_STOP_TIMEOUT_MS = 30_000;

/**
 * Hard stop: silence remote audio immediately, finalize client archive, then hangup.
 * Order matters — hangup/stopFullSession must run only after archive stop() so
 * LaRoom tracks stay alive for MediaRecorder finalize (parallel hangup caused empty S3 slots).
 * Marks intentional hangup in control-api so the worker does not treat this as
 * reconnect-grace abandon (`didnt_come_back`). Hangup also stops the LA session.
 */
export type SessionEndReason = 'hangup' | 'session_limit';

export async function endSession(
  handles: SessionHandles | null,
  opts?: {
    transcriptLines?: Array<{ role: string; text: string; at?: number }>;
    endReason?: SessionEndReason;
    explicitUserHangup?: boolean;
  },
) {
  if (!handles) return;
  const { room, sessionId, pipecatClient } = handles;

  handles.stopTimers?.();

  const operationId = `end-${Date.now()}`;
  handles.emitDiagEndFlow?.({
    origin: 'endSession',
    caller_branch: 'explicit_or_automatic_cleanup',
    operation_id: operationId,
    explicit_button: false,
    note: 'caller_must_set_explicit_button_true_from_UI_when_known',
  });

  const endReason = opts?.endReason;
  const hangupBody: {
    origin: string;
    client_operation_id: string;
    endReason?: SessionEndReason;
    explicit_user_hangup?: boolean;
    transcriptLines?: Array<{ role: string; text: string; at?: number }>;
  } = {
    origin: endReason === 'session_limit' ? 'browser.session_limit' : 'browser.endSession',
    client_operation_id: operationId,
  };
  if (endReason) hangupBody.endReason = endReason;
  if (opts?.explicitUserHangup === true) hangupBody.explicit_user_hangup = true;
  if (
    String(handles.agentMode || '').toLowerCase() === 'liveavatar'
    && Array.isArray(opts?.transcriptLines)
    && opts.transcriptLines.length
  ) {
    hangupBody.transcriptLines = opts.transcriptLines;
  }

  // 1. Instant silence BEFORE archive wait / network — complete screen must stay quiet.
  silenceSessionAudio(handles);

  // 2. Finalize client archive while LaRoom tracks are still up (before stopFullSession).
  const archiveHandle = handles.clientArchive;
  handles.clientArchive = null;
  if (archiveHandle) {
    try {
      await Promise.race([
        archiveHandle.stop(),
        new Promise<void>((_resolve, reject) => {
          window.setTimeout(
            () => reject(new Error('client archive stop timeout')),
            CLIENT_ARCHIVE_STOP_TIMEOUT_MS,
          );
        }),
      ]);
    } catch (e) {
      debugLog('client.archive_stop_failed', {
        sessionId,
        error: String((e as Error)?.message || e),
      });
      if (sessionId) {
        void api
          .reportClientEvent(sessionId, {
            type: 'client.archive_error',
            message: String((e as Error)?.message || e),
            meta: { phase: 'endSession.stop' },
          })
          .catch(() => undefined);
      }
    }
  }

  // 3. Hangup stops LiveAvatar FULL server-side (must not run before archive finalize).
  if (sessionId) {
    await api.hangupSession(sessionId, hangupBody).catch(() => undefined);
  }

  // 4. Leave room — prefer PipecatClient disconnect when present
  try {
    if (pipecatClient) {
      await pipecatClient.disconnect();
    } else {
      await room.disconnect(true);
    }
  } catch {
    /* ignore */
  } finally {
    handles.stopTimers?.();
    detachAllRemote(room);
    stopAllLocalTracks(room);
  }
  cleanupRemoteAudio();
}
